import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { AlertCircle, Lock, Mail, ShieldCheck, ArrowLeft, ArrowRight } from "lucide-react";
import { apiCall, login, type ApiError } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";

// rc89 — Login rediseñado "Halo Zafiro" (match del mockup autorizado
// _design/byteisp-login-halo-zafiro-v1.html). Página dark standalone con
// fondo animado de "flujo de recaudación" (bandas SVG + pings zafiro),
// hero con features a la izquierda y card de acceso a la derecha.
// La LÓGICA de auth (credenciales → TFA → change-password) es la misma
// de siempre; solo cambió la presentación. Paleta hardcodeada porque el
// login vive fuera del theming (pre-auth).
// Responsive: en móvil la card va PRIMERO (order) pa' que el form quede
// arriba del fold; el hero baja. prefers-reduced-motion apaga animaciones.

type MeResponse = {
  id: string;
  email: string;
  nombre_completo: string | null;
  rol: { id: string; nombre: string; tipo: string } | null;
  permissions: string[];
  tfa_enabled: boolean;
  last_login_at: string | null;
};

const schema = z.object({
  email: z.string().email("Email inválido"),
  password: z.string().min(1, "Ingresa tu password"),
});

type FormValues = z.infer<typeof schema>;

type Step = "credentials" | "tfa";

// Sparkline compartida de las bandas del fondo (del mockup)
const SPARK = "0,60 50,52 100,64 150,46 200,58 250,40 300,54 350,62 400,44 450,56 500,36 550,50 600,60 650,48 700,58 750,42 800,54 850,64 900,50 950,58 1000,42 1050,56 1100,48 1150,54 1200,60";
const AREA_LINE = "0,70 40,66 80,72 120,58 160,64 200,50 240,60 280,44 320,56 360,38 400,52 440,60 480,48 520,66 560,54 600,62 640,42 680,58 720,36 760,50 800,58 840,46 880,64 920,52 960,60 1000,44 1040,56 1080,50 1120,62 1160,66 1200,70";
const AREA_POLY = `${AREA_LINE} 1200,120 0,120`;

function BandSvg({ dashed, area }: { dashed?: boolean; area?: boolean }) {
  return (
    <svg width="1200" height="120" viewBox="0 0 1200 120" fill="none" className="flex-none">
      {area && <polygon points={AREA_POLY} fill="rgba(37,99,235,.05)" />}
      <polyline
        points={area ? AREA_LINE : SPARK}
        stroke="rgba(124,166,245,1)"
        strokeWidth="1.5"
        strokeDasharray={dashed ? "5 6" : undefined}
      />
    </svg>
  );
}

function Band({ style, dashed, area, className }: { style: React.CSSProperties; dashed?: boolean; area?: boolean; className?: string }) {
  return (
    <div className={`halo-band absolute left-0 flex w-[2400px] will-change-transform ${className ?? ""}`} style={style}>
      <BandSvg dashed={dashed} area={area} />
      <BandSvg dashed={dashed} area={area} />
    </div>
  );
}

const FEATURES: { title: string; desc: string; icon: React.ReactNode }[] = [
  {
    title: "Facturación CFDI 4.0",
    desc: "Timbrado automático ante el SAT, masivo y por cliente.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]">
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
        <path d="M14 3v5h5" /><path d="M9 13h6M9 17h4" />
      </svg>
    ),
  },
  {
    title: "Pagos en tiempo real",
    desc: "SPEI y conciliación instantánea con integración Toku.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]">
        <path d="M13 2 4.5 13.5H11L10 22l8.5-11.5H12z" />
      </svg>
    ),
  },
  {
    title: "Cobranza y suspensiones",
    desc: "Corte y reactivación automática directo en tu red.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]">
        <path d="M3 12a9 9 0 0 1 15.5-6.2M21 12a9 9 0 0 1-15.5 6.2" />
        <path d="M18.5 2v4h-4M5.5 22v-4h4" />
      </svg>
    ),
  },
  {
    title: "Portal del cliente",
    desc: "Autoservicio: facturas, pagos en línea y soporte.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]">
        <circle cx="12" cy="8" r="3.5" />
        <path d="M5 20a7 7 0 0 1 14 0" />
      </svg>
    ),
  },
];

export function LoginPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<Step>("credentials");
  const [tfaCode, setTfaCode] = useState("");
  const [showPass, setShowPass] = useState(false);
  // F107 — guardamos credenciales del paso 1 para reusar en paso 2
  const [stashedCreds, setStashedCreds] = useState<FormValues | null>(null);

  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const setChangeToken = useAuthStore((s) => s.setChangeToken);
  const setUser = useAuthStore((s) => s.setUser);
  const setPermissions = useAuthStore((s) => s.setPermissions);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },  // rc129: sin cuenta prellenada
  });

  async function processLoginResponse(
    resp: Awaited<ReturnType<typeof login>>,
    creds: FormValues,
  ) {
    if (resp.token_type === "bearer") {
      setAccessToken(resp.access_token);
      try {
        const me = await apiCall<MeResponse>("get", "/api/v1/me");
        setUser({
          id: me.id, email: me.email, nombre_completo: me.nombre_completo,
          rol: me.rol ? { id: me.rol.id, nombre: me.rol.nombre } : null,
        });
        setPermissions(me.permissions);
      } catch {
        setPermissions([]);
      }
      navigate("/", { replace: true });
    } else if (resp.token_type === "password_change_required") {
      setChangeToken(resp.token);
      navigate("/change-password", {
        replace: true,
        state: { email: creds.email, currentPassword: creds.password },
      });
    } else if (resp.token_type === "tfa_challenge") {
      // F107 — pasar al paso 2
      setStashedCreds(creds);
      setStep("tfa");
      setError(null);
    } else {
      setError("Respuesta inesperada del servidor.");
    }
  }

  async function onSubmitCreds(values: FormValues) {
    setError(null);
    setSubmitting(true);
    try {
      const resp = await login(values.email, values.password);
      await processLoginResponse(resp, values);
    } catch (e) {
      const api = e as ApiError;
      setError(api.message ?? "Error al iniciar sesión");
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitTfa(e: React.FormEvent) {
    e.preventDefault();
    if (!stashedCreds || !tfaCode.trim()) return;
    setError(null);
    setSubmitting(true);
    try {
      const resp = await login(
        stashedCreds.email,
        stashedCreds.password,
        tfaCode.trim(),
      );
      await processLoginResponse(resp, stashedCreds);
    } catch (e) {
      const api = e as ApiError;
      setError(api.message ?? "Código inválido");
    } finally {
      setSubmitting(false);
    }
  }

  function backToCredentials() {
    setStep("credentials");
    setTfaCode("");
    setStashedCreds(null);
    setError(null);
  }

  const inputCls =
    "h-[46px] w-full rounded-[10px] border border-white/[0.08] bg-[#09090b] pl-10 pr-14 text-[13.5px] text-[#f4f4f5] placeholder:text-[#67676f] outline-none transition-colors focus:border-[#2563eb]";
  const errBoxCls =
    "flex items-start gap-2 rounded-[10px] border border-[#e5484d]/50 bg-[#e5484d]/10 p-2.5 text-xs text-[#f0868a]";

  return (
    <div className="relative flex min-h-full flex-col overflow-hidden bg-[#09090b] px-5 py-5 text-[#f4f4f5] sm:px-12 sm:py-6">
      <style>{`
        @keyframes halo-flow { to { transform: translateX(-1200px); } }
        @keyframes halo-ping { 0% { box-shadow: 0 0 0 0 rgba(37,99,235,.35); } 70% { box-shadow: 0 0 0 12px transparent; } 100% { box-shadow: 0 0 0 0 transparent; } }
        @media (prefers-reduced-motion: reduce) { .halo-band, .halo-ping { animation: none !important; } }
      `}</style>

      {/* ── fondo: flujo de operación (recaudación en vivo) ── */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <Band className="hidden sm:flex" dashed style={{ top: "15%", opacity: 0.10, animation: "halo-flow 115s linear infinite" }} />
        <Band area style={{ top: "45%", opacity: 0.13, animation: "halo-flow 75s linear infinite" }} />
        <Band style={{ bottom: "7%", opacity: 0.08, animation: "halo-flow 48s linear infinite" }} />
        <span className="halo-ping absolute h-[5px] w-[5px] rounded-full bg-[#7ca6f5]/55" style={{ top: "18%", left: "24%", animation: "halo-ping 3.6s ease-out infinite" }} />
        <span className="halo-ping absolute hidden h-[5px] w-[5px] rounded-full bg-[#7ca6f5]/55 sm:block" style={{ top: "48%", left: "60%", animation: "halo-ping 3.6s ease-out infinite", animationDelay: "1.2s" }} />
        <span className="halo-ping absolute h-[5px] w-[5px] rounded-full bg-[#7ca6f5]/55" style={{ top: "74%", left: "36%", animation: "halo-ping 3.6s ease-out infinite", animationDelay: "2.4s" }} />
        <span className="halo-ping absolute hidden h-[5px] w-[5px] rounded-full bg-[#7ca6f5]/55 sm:block" style={{ top: "30%", left: "78%", animation: "halo-ping 3.6s ease-out infinite", animationDelay: ".7s" }} />
      </div>

      {/* ── top ── */}
      <header className="relative z-10 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center gap-[2.5px] rounded-[9px] bg-[#2563eb]/[0.14]">
            <span className="block h-[10px] w-[3.5px] rounded-full bg-[#7ca6f5] opacity-80" />
            <span className="block h-[15px] w-[3.5px] rounded-full bg-[#7ca6f5]" />
            <span className="block h-[7px] w-[3.5px] rounded-full bg-[#7ca6f5] opacity-55" />
          </div>
          <b className="text-base font-bold tracking-tight">
            Byte<span className="text-[#7ca6f5]">ISP</span>
          </b>
        </div>
        <div className="flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[.14em] text-[#67676f]">
          <span className="halo-pulse inline-block h-[6px] w-[6px] rounded-full bg-[#2563eb]" />
          conexión segura
        </div>
      </header>

      {/* ── main ── */}
      <main className="relative z-10 mx-auto grid w-full max-w-[1360px] flex-1 items-center gap-8 py-6 lg:grid-cols-[1.15fr_.85fr] lg:gap-16 lg:py-8">
        {/* hero — en móvil va después de la card */}
        <section className="order-2 lg:order-1">
          <div className="flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[.18em] text-[#7ca6f5]">
            operación en vivo <span className="text-[#67676f]">·</span> acceso
          </div>
          <h1 className="my-4 text-[clamp(32px,6.5vw,64px)] font-extrabold leading-[1.05] tracking-[-.035em] lg:mb-5 lg:leading-[1.03]">
            Tu ISP,<br />bajo control.
          </h1>
          <p className="mb-9 max-w-[46ch] text-[14.5px] leading-relaxed text-[#a1a1aa] lg:text-base">
            Clientes, facturación CFDI, cobranza, pasarelas de pago y suspensiones
            automáticas. Toda la gestión de tu ISP en un solo lugar.
          </p>

          <div className="grid max-w-[640px] gap-2.5 sm:grid-cols-2">
            {FEATURES.map((f) => (
              <div key={f.title} className="flex items-start gap-3 rounded-xl border border-white/[0.045] bg-[#121214] px-4 py-[15px]">
                <div className="flex h-9 w-9 flex-none items-center justify-center rounded-[10px] border border-white/[0.045] bg-[#2563eb]/[0.14] text-[#7ca6f5]">
                  {f.icon}
                </div>
                <div>
                  <b className="mb-0.5 block text-[13.5px] font-semibold">{f.title}</b>
                  <span className="block text-[11.5px] leading-relaxed text-[#a1a1aa]">{f.desc}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3.5 flex flex-wrap items-center gap-2">
            <span className="mr-1 font-mono text-[10px] uppercase tracking-[.18em] text-[#67676f]">Integraciones</span>
            {["TOKU", "SPEI", "SAT · CFDI 4.0", "MIKROTIK"].map((i) => (
              <span key={i} className="rounded-md border border-white/[0.045] bg-white/[0.04] px-2.5 py-[5px] font-mono text-[9.5px] font-bold tracking-[.08em] text-[#f4f4f5]">
                {i}
              </span>
            ))}
          </div>
        </section>

        {/* card — en móvil va primero */}
        <div className="order-1 mx-auto w-full max-w-[440px] rounded-2xl border border-white/[0.08] bg-[#121214] p-6 sm:p-8 lg:order-2 lg:mx-0 lg:justify-self-end">
          <div className="mb-6 flex items-center gap-3 border-b border-white/[0.045] pb-5">
            <div className="flex h-9 w-9 items-center justify-center rounded-[9px] border border-white/[0.08] bg-[#18181b] text-[11px] font-bold">
              BI
            </div>
            <div>
              <b className="block text-sm font-semibold">Portal administrativo</b>
              <span className="mt-0.5 block text-[11.5px] text-[#67676f]">
                Acceso exclusivo para personal autorizado
              </span>
            </div>
          </div>

          {step === "credentials" && (
            <form onSubmit={handleSubmit(onSubmitCreds)}>
              <div className="mb-2.5 font-mono text-[10.5px] uppercase tracking-[.18em] text-[#7ca6f5]">bienvenido</div>
              <h2 className="text-[28px] font-bold tracking-[-.025em]">Inicia sesión</h2>
              <p className="mb-7 mt-1.5 text-[13.5px] text-[#a1a1aa]">Continúa hacia tu centro de control.</p>

              <label htmlFor="email" className="mb-2 block text-[12.5px] font-semibold">Correo electrónico</label>
              <div className="relative mb-1">
                <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-[#67676f]" />
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  placeholder="nombre@bytecom.mx"
                  className={inputCls}
                  {...register("email")}
                />
              </div>
              {errors.email && <span className="text-xs text-[#f0868a]">{errors.email.message}</span>}

              <label htmlFor="password" className="mb-2 mt-[18px] block text-[12.5px] font-semibold">Contraseña</label>
              <div className="relative mb-1">
                <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-[#67676f]" />
                <input
                  id="password"
                  type={showPass ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="••••••••••••"
                  className={inputCls}
                  {...register("password")}
                />
                <button
                  type="button"
                  onClick={() => setShowPass((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[11.5px] font-semibold text-[#67676f] hover:text-[#f4f4f5]"
                >
                  {showPass ? "Ocultar" : "Ver"}
                </button>
              </div>
              {errors.password && <span className="text-xs text-[#f0868a]">{errors.password.message}</span>}

              {error && (
                <div className={`${errBoxCls} mt-3`}>
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="group mt-5 flex h-12 w-full items-center justify-center gap-2.5 rounded-[10px] bg-[#2563eb] text-sm font-bold text-white transition-all hover:-translate-y-px hover:bg-[#3373f2] disabled:pointer-events-none disabled:opacity-60"
              >
                {submitting ? "Verificando…" : "Entrar a ByteISP"}
                {!submitting && (
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                )}
              </button>
            </form>
          )}

          {step === "tfa" && (
            <form onSubmit={onSubmitTfa}>
              <div className="mb-2.5 font-mono text-[10.5px] uppercase tracking-[.18em] text-[#7ca6f5]">verificación</div>
              <h2 className="text-[28px] font-bold tracking-[-.025em]">Dos factores</h2>
              <p className="mb-6 mt-1.5 text-[13.5px] text-[#a1a1aa]">Un paso más para entrar.</p>

              <div className="mb-4 flex items-start gap-2 rounded-[10px] border border-[#2563eb]/30 bg-[#2563eb]/[0.07] p-2.5 text-[11px] text-[#a1a1aa]">
                <ShieldCheck size={14} className="mt-0.5 shrink-0 text-[#7ca6f5]" />
                <span>
                  Mete el código de 6 dígitos de tu Authenticator
                  <span className="mt-0.5 block">
                    O un backup code (XXXX-XXXX) si perdiste el dispositivo.
                  </span>
                </span>
              </div>

              <label htmlFor="tfa" className="mb-2 block text-[12.5px] font-semibold">Código</label>
              <input
                id="tfa"
                inputMode="text"
                autoComplete="one-time-code"
                value={tfaCode}
                onChange={(e) => setTfaCode(e.target.value)}
                placeholder="123456 o XXXX-XXXX"
                autoFocus
                className={`${inputCls} pl-4 pr-4 text-center font-mono text-lg tracking-wider`}
              />

              {error && (
                <div className={`${errBoxCls} mt-3`}>
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !tfaCode.trim()}
                className="mt-5 flex h-12 w-full items-center justify-center gap-2.5 rounded-[10px] bg-[#2563eb] text-sm font-bold text-white transition-all hover:-translate-y-px hover:bg-[#3373f2] disabled:pointer-events-none disabled:opacity-60"
              >
                {submitting ? "Verificando…" : "Verificar y entrar"}
              </button>
              <button
                type="button"
                onClick={backToCredentials}
                disabled={submitting}
                className="mt-2.5 flex h-9 w-full items-center justify-center gap-1.5 rounded-[10px] text-xs font-medium text-[#a1a1aa] transition-colors hover:text-[#f4f4f5] disabled:opacity-60"
              >
                <ArrowLeft size={12} /> Volver
              </button>
            </form>
          )}

          <div className="mt-6 flex items-center justify-between border-t border-white/[0.045] pt-4 font-mono text-[10px] text-[#67676f]">
            <span className="flex items-center gap-[7px]">
              <span className="inline-block h-[5px] w-[5px] rotate-45 bg-[#2563eb]" />
              cifrado de extremo a extremo
            </span>
            <span>v{__APP_VERSION__}</span>
          </div>
        </div>
      </main>

      {/* ── footer ── */}
      <footer className="relative z-10 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 font-mono text-[10px] text-[#67676f] md:text-[11px]">
        <span>© 2026 ByteISP · DASO Systems</span>
        <span>{window.location.host}</span>
      </footer>
    </div>
  );
}
